import { Injectable, OnDestroy } from '@angular/core';
import { Subject, interval, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { Settings } from '@stockcare-helpers/settings';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { AuthenticationStateService } from '@stockcare-services/authentication/authentication-state.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationUpdateService implements OnDestroy {
  applicationUpdateCheckInterval = new Subject();
  subscriptions = new Subscription();
  isLoggedIn: boolean = false;
  constructor(
    private swUpdate: SwUpdate,
    private notify: NotificationService,
    private settingsService: SettingsService,
    authenticationStateService: AuthenticationStateService
  ) {
    this.subscriptions.add(
      authenticationStateService.isLoggedIn().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;

        if (!this.isLoggedIn) {
          this.reloadIfNecessary();
        }
      })
    );
  }

  initialize() {
    console.log('Subscribed to application updates');
    this.subscriptions.add(
      this.swUpdate.versionUpdates.pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY')).subscribe(
        () => {
          console.log('New version found');
          localStorage.setItem('reloadOnLogout', 'true');
          if (!this.isLoggedIn) {
            this.reloadIfNecessary();
          }
        },
        (error) => {
          this.notify.error(error, 'A new version of the app could not be installed');
        },
        () => {}
      )
    );

    this.checkForApplicationUpdates();
  }

  private checkForApplicationUpdates() {
    this.subscriptions.add(
      this.applicationUpdateCheckInterval
        .pipe(switchMap((applicationUpdateCheckInterval: number) => interval(applicationUpdateCheckInterval)))
        .subscribe(() => {
          console.log('Checking for new version');
          this.checkForForcedUpdate();
          if (this.swUpdate.isEnabled) {
            this.swUpdate
              .checkForUpdate()
              .then(() => {
                console.log('Checked for new version');
              })
              .catch((error) => console.log(error));
          } else {
            console.log('Cant check for update. Service worker is disabled');
          }

          const applicationUpdateInterval = this.settingsService.getSettingValue(Settings.APPLICATION_UPDATE_INTERVAL) * 1000;
          this.applicationUpdateCheckInterval.next(applicationUpdateInterval);
        })
    );

    this.applicationUpdateCheckInterval.next(1000);
  }

  private checkForForcedUpdate() {
    const forcedUpdateVersion = this.settingsService.getSettingValue(Settings.FORCE_CLIENT_UPDATE);

    const forceUpdateVersionInLocalStorage = localStorage.getItem('forcedUpdateVersion');

    if (forceUpdateVersionInLocalStorage === null) {
      localStorage.setItem('forcedUpdateVersion', forcedUpdateVersion);
    } else if (forcedUpdateVersion !== forceUpdateVersionInLocalStorage) {
      localStorage.setItem('reloadOnLogout', 'true');
      localStorage.setItem('forcedUpdateVersion', forcedUpdateVersion);
    }
  }

  reloadIfNecessary() {
    if (localStorage.getItem('reloadOnLogout') !== null) {
      this.reload();
      console.log('Reloading on logout');
    }
  }

  reload() {
    document.location.reload();
    this.removeReloadOnLogout();
  }

  removeReloadOnLogout() {
    localStorage.removeItem('reloadOnLogout');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
